import {MediaMatcher} from '@angular/cdk/layout';
import {
  ChangeDetectorRef,
  Component, ElementRef, EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {StartupService} from "../../../service-ui/startup.service";
import {LoggedInCallback} from "../../../service-auth/cognito.service";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {Book} from "../../../../model/Book";
import {Product} from "../../../../model/Product";
import {Resource} from "../../../../model/Resource";
import {BookService} from "../../../service-data/book.service";
import {CoverService} from "../../../service-data/cover.service";
import {BodyService} from "../../../service-data/body.service";
import {ResourceStatus} from "../../../service-ui/resource-status";
import {StageWatch} from "../../../service-ui/staged-preview-image";
import {ProductService} from "../../../service-data/product.service";
import {ResourceService} from "../../../service-data/resource.service";
import {PaperTypeService} from "../../../service-data/paper-type.service";
import {LanguagePipe} from "../../../pipe/language.pipe";
import {LANGUAGES, LanguageEntry} from "../../../../model/Languages";

@Component({
  selector: 'app-project-book',
  templateUrl: './project-book.component.html',
  styleUrls: ['./project-book.component.css']
})
export class ProjectBookComponent implements OnDestroy, OnInit, LoggedInCallback {
  @Input() book: Book;
  @Input() displayCover: boolean;
  @Input() editingDetails: string;
  @Input() displaySettings: boolean;
  @Output() editingDetailsChange = new EventEmitter<any>();
  @Output() displaySettingsChange = new EventEmitter<boolean>();
  @Output() displayCoverChange = new EventEmitter<boolean>();
  @ViewChild('coverPreviewArea') coverPreviewArea: ElementRef;
  @ViewChild('spreadsPreviewArea') spreadsPreviewArea: ElementRef;
  @ViewChild('coverContent') coverContent: ElementRef;
  @ViewChild('interiorContent') interiorContent: ElementRef;

  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;
  onDestroySubject: Subject<boolean> = new Subject();

  /** Suppresses preview work on Cover/Body updates that change no artefact. */
  private previewStageWatch = new StageWatch();

  showProductEditor: boolean = true;
  showImmutableProps: boolean = true;

  // Dirty tracking for Save/Cancel
  private bookSnapshot: any = {};
  private productSnapshot: any = {};
  isDirty: boolean = false;
  editingTitle: boolean = false;

  // Zoom controls
  coverZoom: number = 100;
  interiorZoom: number = 100;

  // Page range for interior
  interiorPageStart: number = 1;
  interiorPageEnd: number = 10;
  readonly PAGE_CHUNK = 10;

  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link']
    ]
  };

  constructor(
    public router: Router,
    public startupService: StartupService,
    public changeDetectorRef: ChangeDetectorRef,
    public media: MediaMatcher,
    private bookService: BookService,
    private coverService: CoverService,
    private bodyService: BodyService,
    public productService: ProductService,
    public resourceService: ResourceService,
    public paperTypeService: PaperTypeService,
    private route: ActivatedRoute,
    @Inject(LOCALE_ID) localeId: string
  ) {
    this.startupService.touchLastUserActivity();
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  ngOnInit() {
    this.startupService.isAuthenticated(this);

    // Watch `Cover.standardized` / `Body.standardized`.
    //
    // The `*ngIf` getters above already make the preview sections appear when
    // these flip to COMPLETE, because change detection re-evaluates a getter.
    // This subscription covers the case the getters cannot: the section is
    // ALREADY visible and the artefact is standardized again (a re-request
    // after an ERROR, or a corrected upload). The `*ngIf` never changes, the
    // child component is never recreated, and without this the operator keeps
    // looking at the superseded render.
    //
    // Dropping the cached images is the whole job - the preview components
    // re-request on their own next pass, and they key their cache on the
    // artefact stage, so this only has to force the miss.
    this.coverService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(cover => {
        if (cover && cover.getId && this.book && this.book.cover
          && cover.getId() === this.book.cover.ID) {
          this.onStandardizedChanged();
        }
      });

    this.bodyService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(body => {
        if (body && body.getId && this.book && this.book.body
          && body.getId() === this.book.body.ID) {
          this.onStandardizedChanged();
        }
      });

    setTimeout(() => {
      this.measureAndFitCover();
      this.measureAndFitInterior();
      this.takeSnapshot();
    }, 500);
    // Retry after images may have loaded
    setTimeout(() => {
      this.measureAndFitCover();
      this.measureAndFitInterior();
    }, 1500);
    setTimeout(() => {
      this.measureAndFitCover();
      this.measureAndFitInterior();
    }, 3000);
  }

  /**
   * A Cover or Body row changed. Re-measure, and let the previews re-fetch.
   *
   * Only acts when the stage actually moved. `updateNotifier` fires for every
   * row of its type and notifications are broadcast to every session on the
   * role (`shared/Notifications.md` §5), so an unfiltered reaction here would
   * re-request both previews whenever anyone edited any book's shift values.
   */
  private onStandardizedChanged(): void {
    const status = {
      resourceId: (this.book && this.book.body && this.book.body.resource_ID) || 0,
      standardized: this.book && this.book.body ? this.book.body.standardized : undefined,
      standardizes: true
    };
    if (!this.previewStageWatch.changed(status)) {
      return;
    }
    // The zoom-to-fit maths reads the rendered image size, so it has to run
    // again once a different render lands.
    setTimeout(() => {
      this.measureAndFitCover();
      this.measureAndFitInterior();
    }, 500);
  }

  /** Take a snapshot of current Book + Product values for dirty tracking */
  takeSnapshot(): void {
    if (this.book) {
      this.bookSnapshot = {
        title: this.book.title, subtitle: this.book.subtitle, author: this.book.author,
        isbn: this.book.isbn, publicationYear: this.book.publicationYear, sfbn: this.book.sfbn,
        languageCode: this.book.languageCode, msrp: this.book.msrp, caseQuantity: this.book.caseQuantity,
        tagId: this.book.tagId, comment: this.book.comment
      };
    }
    const p = this.product;
    if (p) {
      this.productSnapshot = {
        customerProduct_ID: p.customerProduct_ID, EAN13: p.EAN13, title: p.title,
        subtitle: p.subtitle, shortDescription: p.shortDescription, description: p.description,
        languageCode: p.languageCode, msrp: p.msrp, msrpCurrency: p.msrpCurrency,
        upstreamCost: p.upstreamCost, previewPages: p.previewPages
      };
    }
    this.isDirty = false;
  }

  /** Check if values have changed from the snapshot */
  markDirty(): void {
    if (!this.book) {
      return;
    }
    const b = this.book;
    const bs = this.bookSnapshot;
    let dirty = b.title !== bs.title || b.subtitle !== bs.subtitle || b.author !== bs.author ||
      b.isbn !== bs.isbn || b.publicationYear !== bs.publicationYear || b.sfbn !== bs.sfbn ||
      b.languageCode !== bs.languageCode || b.msrp !== bs.msrp || b.caseQuantity !== bs.caseQuantity ||
      b.tagId !== bs.tagId || b.comment !== bs.comment;

    const p = this.product;
    if (p) {
      const ps = this.productSnapshot;
      dirty = dirty || p.customerProduct_ID !== ps.customerProduct_ID || p.EAN13 !== ps.EAN13 ||
        p.title !== ps.title || p.subtitle !== ps.subtitle || p.shortDescription !== ps.shortDescription ||
        p.description !== ps.description || p.languageCode !== ps.languageCode || p.msrp !== ps.msrp ||
        p.msrpCurrency !== ps.msrpCurrency || p.upstreamCost !== ps.upstreamCost || p.previewPages !== ps.previewPages;
    }
    this.isDirty = dirty;
  }

  /** Save all changes and take new snapshot */
  saveAll(): void {
    this.saveBook();
    this.saveProduct();
    this.takeSnapshot();
  }

  /** Revert all changes to the last snapshot */
  cancelChanges(): void {
    if (this.book) {
      const bs = this.bookSnapshot;
      this.book.title = bs.title;
      this.book.subtitle = bs.subtitle;
      this.book.author = bs.author;
      this.book.isbn = bs.isbn;
      this.book.publicationYear = bs.publicationYear;
      this.book.sfbn = bs.sfbn;
      this.book.languageCode = bs.languageCode;
      this.book.msrp = bs.msrp;
      this.book.caseQuantity = bs.caseQuantity;
      this.book.tagId = bs.tagId;
      this.book.comment = bs.comment;
    }
    const p = this.product;
    if (p) {
      const ps = this.productSnapshot;
      p.customerProduct_ID = ps.customerProduct_ID;
      p.EAN13 = ps.EAN13;
      p.title = ps.title;
      p.subtitle = ps.subtitle;
      p.shortDescription = ps.shortDescription;
      p.description = ps.description;
      p.languageCode = ps.languageCode;
      p.msrp = ps.msrp;
      p.msrpCurrency = ps.msrpCurrency;
      p.upstreamCost = ps.upstreamCost;
      p.previewPages = ps.previewPages;
    }
    this.isDirty = false;
  }

  private measureAndFitCover(): void {
    if (this.coverPreviewArea) {
      const intrinsicW = this.coverIntrinsicWidth;
      const containerW = this.coverPreviewArea.nativeElement.clientWidth - 32;
      if (intrinsicW > 0 && containerW > 0) {
        this.coverZoom = Math.max(25, Math.min(800, Math.floor((containerW / intrinsicW) * 100)));
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  private measureAndFitInterior(): void {
    if (this.spreadsPreviewArea) {
      const intrinsicW = this.interiorIntrinsicWidth;
      const containerW = this.spreadsPreviewArea.nativeElement.clientWidth - 32;
      if (intrinsicW > 0 && containerW > 0) {
        this.interiorZoom = Math.max(25, Math.min(800, Math.floor((containerW / intrinsicW) * 100)));
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  onDataChange() {
    this.editingDetailsChange.emit(this.editingDetails);
    this.displaySettingsChange.emit(this.displaySettings);
    this.displayCoverChange.emit(this.displayCover);
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      this.router.navigate(['/home/login']);
    } else {
      this.startupService.touchLastUserActivity();
    }
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
  }

  // --- Product ---

  get product(): Product {
    if (!this.book) {
      return null;
    }
    return this.productService.objects.find(p => p.book_ID === this.book.ID) || null;
  }

  /** Save shared fields to both Book and Product (no auto-persist, just update models) */
  saveSharedTitle(value: string): void {
    if (this.book) { this.book.title = value; }
    const p = this.product;
    if (p) { p.title = value; }
    this.markDirty();
  }

  startEditTitle() {
    this.editingTitle = true;
    setTimeout(() => {
      const input = document.querySelector('.inline-title-input') as HTMLInputElement;
      if (input) { input.focus(); input.select(); }
    });
  }

  commitTitle(value: string) {
    this.editingTitle = false;
    const trimmed = value.trim();
    if (trimmed && trimmed !== this.book.title) {
      this.saveSharedTitle(trimmed);
    }
  }

  cancelTitle(input: HTMLInputElement) {
    input.value = this.book.title || '';
    this.editingTitle = false;
  }

  saveSharedSubtitle(value: string): void {
    if (this.book) { this.book.subtitle = value; }
    const p = this.product;
    if (p) { p.subtitle = value; }
    this.markDirty();
  }

  saveSharedAuthor(value: string): void {
    if (this.book) { this.book.author = value; }
    const p = this.product;
    if (p) { p.productAuthor = value; }
    this.markDirty();
  }

  saveSharedIsbn(value: string): void {
    if (this.book) { this.book.isbn = value; }
    const p = this.product;
    if (p) { p.EAN13 = value; }
    this.markDirty();
  }

  saveSharedPublicationYear(value: number): void {
    if (this.book) { this.book.publicationYear = value; }
    const p = this.product;
    if (p) { p.productPublicationYear = value; }
    this.markDirty();
  }

  // ── Language combo box support ─────────────────────────────
  // Central language catalogue (src/model/Languages.ts). See
  // ProductComponent for the same helpers used by the standalone
  // product editor. Entries with `translatable: true` are routed
  // through LanguagePipe so the display label follows the current
  // UI locale.
  languages: LanguageEntry[] = LANGUAGES;
  languageFilter: string = '';

  languageDisplayName(entry: LanguageEntry): string {
    if (!entry) { return ''; }
    return entry.translatable ? LanguagePipe.get(entry.name, 'compressSpaces') : entry.name;
  }

  filteredLanguages(): LanguageEntry[] {
    const f = (this.languageFilter || '').toString().trim().toLowerCase();
    if (!f) { return this.languages; }
    return this.languages.filter(l =>
      l.iso639_3.toLowerCase().indexOf(f) === 0 ||
      (l.iso639_2 && l.iso639_2.toLowerCase().indexOf(f) === 0) ||
      this.languageDisplayName(l).toLowerCase().indexOf(f) !== -1
    );
  }

  /**
   * Keep the book's and product's language codes in sync when
   * either one is edited. Book.languageCode is the source of truth
   * for the mutablebook row; Product.languageCode mirrors it on
   * the product row so downstream consumers (ecommerce feeds,
   * order placement, etc.) see the same value.
   */
  saveSharedLanguageCode(value: string): void {
    const normalized = (value || '').toString().trim().toLowerCase();
    if (this.book) { this.book.languageCode = normalized; }
    const p = this.product;
    if (p) { p.languageCode = normalized; }
    this.markDirty();
  }

  private saveBoth(): void {
    this.saveBook();
    this.saveProduct();
  }

  saveProduct(): void {
    const p = this.product;
    if (p) {
      this.productService.updateItem(p).subscribe();
    }
  }

  saveBook(): void {
    if (this.book) {
      this.bookService.updateItem(this.book).subscribe();
    }
  }

  // --- Resource helpers ---
  //
  // `Cover.resource` / `Body.resource` are declared on the models but never
  // populated: `Cover.update()` copies only primitives (string/number/boolean/
  // Date) and skips nested objects, so the field stays undefined no matter what
  // the server nests in the payload. Every read therefore falls through to
  // ResourceService, and ResourceService is not loaded at startup — so the
  // Resource has to be fetched on demand or these getters return null forever.
  //
  // That mattered the moment `canStandardizeCover` started gating on
  // `prepared === 'COMPLETE'`: without the fetch the button would never appear,
  // and the problems panel would never render, both silently. The pre-existing
  // `getResourceStatusColor` / `getResourceHoverText` have the same dependency
  // and now benefit from the same fetch.
  //
  // `requestedResources` is what keeps that from becoming a request storm:
  // these are template getters, re-evaluated on every change-detection pass, so
  // an unguarded `getItem()` would fire one HTTP call per tick until the row
  // arrived. Same guard as ProjectDraftThumbnailComponent.
  private requestedResources: Set<number> = new Set<number>();

  private resourceFor(resource_ID: number): Resource {
    if (!resource_ID) {
      return null;
    }
    const res = this.resourceService.objects.find(r => r.ID === resource_ID);
    if (!res) {
      if (!this.requestedResources.has(resource_ID)) {
        this.requestedResources.add(resource_ID);
        this.resourceService.getItem(resource_ID).subscribe();
      }
      return null;
    }
    return res;
  }

  get coverResource(): Resource {
    if (!this.book || !this.book.cover) {
      return null;
    }
    return this.book.cover.resource || this.resourceFor(this.book.cover.resource_ID);
  }

  get bodyResource(): Resource {
    if (!this.book || !this.book.body) {
      return null;
    }
    return this.book.body.resource || this.resourceFor(this.book.body.resource_ID);
  }

  // --- Preview availability -------------------------------------------
  //
  // 🚨 **The Book editor shows the STANDARDIZED render or nothing.** 🚨
  //
  // This screen is where an operator decides whether a book is fit to print,
  // so the only honest thing to show is the file that will actually be
  // printed - trim + bleed, greyscale where the interior is B&W, crop marks.
  // A prepared or original render looks close enough to be mistaken for it
  // and is exactly the kind of near-miss that gets signed off. Elsewhere in
  // the UI a stand-in beats a blank panel; here it does not.
  //
  // Confirmed by the product owner 2026-09-15, after a brief change in the
  // other direction was reverted. Do not "fix" this by relaxing it to the
  // prepared render - see `shared/PdfArtifactImages.md` §2.
  //
  // ── But it must appear WITHOUT a reload ───────────────────────────────
  // These are getters, not fields, so Angular re-evaluates them on every
  // change-detection pass and the section appears the moment `standardized`
  // flips to COMPLETE. `NotificationsService` applies COVER/BODY updates
  // inside `zone.run(...)`, which ticks change detection, so the sequence is:
  // notification -> `addUpdateLocalItem` mutates the cached Cover/Body ->
  // CD runs -> this getter returns true -> `*ngIf` creates the preview
  // component -> its `ngOnInit` requests the image.
  //
  // That chain is why a *field* would be wrong here. `addUpdateLocalItem`
  // mutates in place, so nothing re-assigns a field, and the preview would
  // stay hidden until the user navigated away and back. The component also
  // subscribes to the two notifiers below, which covers the case where the
  // section is already visible and the artefact is re-standardized.

  /** Is the standardized cover ready to show? Nothing else is shown here. */
  get canPreviewCover(): boolean {
    const side = this.coverSide();
    return !!(side && side.standardized === 'COMPLETE');
  }

  /** Is the standardized interior ready to show? */
  get canPreviewBody(): boolean {
    return !!(this.book && this.book.body && this.book.body.standardized === 'COMPLETE');
  }

  /**
   * Whichever side supplies the cover artwork.
   *
   * A SADDLE_STITCH book has no separate cover - its cover pages come out of
   * the Body PDF - so `Body.standardized` is the status that gates its cover
   * preview.
   */
  private coverSide(): any {
    if (!this.book) { return null; }
    return this.book.bindingType === 'SADDLE_STITCH' ? this.book.body : this.book.cover;
  }

  // --- Standardization / Preparation Status ---

  getStatusLabel(status: string): string {
    switch (status) {
      case 'INITIAL': return LanguagePipe.get('Not Started', 'compressSpaces');
      case 'WAITING': return LanguagePipe.get('Waiting', 'compressSpaces');
      case 'IN_PROGRESS': return LanguagePipe.get('In Progress', 'compressSpaces');
      case 'COMPLETE': return LanguagePipe.get('Complete', 'compressSpaces');
      case 'ERROR': return LanguagePipe.get('Error', 'compressSpaces');
      default: return LanguagePipe.get('Unknown', 'compressSpaces');
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'INITIAL': return 'status-initial';
      case 'WAITING': return 'status-waiting';
      case 'IN_PROGRESS': return 'status-in-progress';
      case 'COMPLETE': return 'status-complete';
      case 'ERROR': return 'status-error';
      default: return 'status-unknown';
    }
  }

  /**
   * Standardization may only be requested once the upload has finished being
   * PREPARED.
   *
   * `Resource.prepared` and `Cover/Body.standardized` are two consecutive
   * server-side stages, not two independent flags: the standardizer reads the
   * prepared artefact. Asking for standardization while `prepared` is still
   * INITIAL/WAITING/IN_PROGRESS queues work against a file that does not exist
   * yet, and the row lands in ERROR for a reason that has nothing to do with
   * the PDF.
   *
   * `ERROR` is deliberately excluded too — a resource that failed preparation
   * has nothing to standardize, and offering the button would invite the user
   * to retry a step that cannot succeed until the upload itself is fixed.
   */
  private canStandardize(side: any, resource: Resource): boolean {
    return !!side && !!resource
      && resource.prepared === 'COMPLETE'
      && side.standardized === 'INITIAL';
  }

  get canStandardizeCover(): boolean {
    return this.canStandardize(this.book && this.book.cover, this.coverResource);
  }

  get canStandardizeBody(): boolean {
    return this.canStandardize(this.book && this.book.body, this.bodyResource);
  }

  /** Request standardization of the cover by moving it INITIAL -> WAITING. */
  standardizeCover(): void {
    if (!this.canStandardizeCover) {
      return;
    }
    this.book.cover.standardized = 'WAITING';
    this.coverService.updateItem(this.book.cover).subscribe();
  }

  /** Request standardization of the body by moving it INITIAL -> WAITING. */
  standardizeBody(): void {
    if (!this.canStandardizeBody) {
      return;
    }
    this.book.body.standardized = 'WAITING';
    this.bodyService.updateItem(this.book.body).subscribe();
  }

  // --- Preparation failures ---

  get coverHasError(): boolean {
    const r = this.coverResource;
    return !!r && r.prepared === 'ERROR';
  }

  get bodyHasError(): boolean {
    const r = this.bodyResource;
    return !!r && r.prepared === 'ERROR';
  }

  /** True when either resource failed preparation, so the panel should render. */
  get hasResourceProblems(): boolean {
    return this.coverHasError || this.bodyHasError
      || !!(this.coverResource && this.coverResource.problemNote)
      || !!(this.bodyResource && this.bodyResource.problemNote);
  }

  /**
   * True if either side is waiting on a step the user can start.
   *
   * Covers two distinct stages, which is why it is not simply
   * `standardized === 'INITIAL'` as it once was: a resource that has not been
   * prepared yet needs `prepared` kicked, and a resource that HAS been prepared
   * needs `standardized` kicked. Under the old test the button appeared for an
   * unprepared resource and set `standardized` to WAITING behind a file that
   * did not exist.
   */
  get canProcessFiles(): boolean {
    if (!this.book || !this.book.cover || !this.book.body) {
      return false;
    }
    const cr = this.coverResource;
    const br = this.bodyResource;
    const needsPreparing = (cr && cr.prepared === 'INITIAL') || (br && br.prepared === 'INITIAL');
    return !!needsPreparing || this.canStandardizeCover || this.canStandardizeBody;
  }

  /** True if cover and body are both COMPLETE for standardized */
  get filesReady(): boolean {
    if (!this.book || !this.book.cover || !this.book.body) {
      return false;
    }
    return this.book.cover.standardized === 'COMPLETE' && this.book.body.standardized === 'COMPLETE';
  }

  /** True if either cover or body is currently being processed */
  get filesProcessing(): boolean {
    if (!this.book || !this.book.cover || !this.book.body) {
      return false;
    }
    const cs = this.book.cover.standardized;
    const bs = this.book.body.standardized;
    return (cs === 'WAITING' || cs === 'IN_PROGRESS' || bs === 'WAITING' || bs === 'IN_PROGRESS');
  }

  /**
   * Kick whichever stage each side is actually ready for.
   *
   * Preparation and standardization are requested independently per side, so a
   * book whose cover is prepared but whose body is not will start
   * standardizing the cover and preparing the body in the same click, rather
   * than incorrectly marking both as standardizing.
   */
  processPdfFiles(): void {
    if (!this.book || !this.book.cover || !this.book.body) {
      return;
    }
    const cr = this.coverResource;
    if (cr && cr.prepared === 'INITIAL') {
      cr.prepared = 'WAITING';
      this.resourceService.updateItem(cr).subscribe();
    }
    const br = this.bodyResource;
    if (br && br.prepared === 'INITIAL') {
      br.prepared = 'WAITING';
      this.resourceService.updateItem(br).subscribe();
    }
    this.standardizeCover();
    this.standardizeBody();
  }

  // --- Download Standardized PDFs ---

  downloadStandardizedCover(): void {
    if (!this.book || !this.book.cover || !this.book.cover.ID) {
      return;
    }
    const filename = (this.book.title || 'cover') + '_cover.pdf';
    this.coverService.getStandardizedURL(this.book.cover.ID, filename).subscribe((url: any) => {
      if (url) {
        const e = document.createEvent('MouseEvents');
        const a = document.createElement('a');
        a.download = filename;
        a.href = url;
        a.dataset.downloadurl = ['application/pdf', a.download, a.href].join(':');
        e.initEvent('click', true, false);
        a.dispatchEvent(e);
      }
    });
  }

  downloadStandardizedBody(): void {
    if (!this.book || !this.book.body || !this.book.body.ID) {
      return;
    }
    const filename = (this.book.title || 'body') + '_body.pdf';
    this.bodyService.getStandardizedURL(this.book.body.ID, filename).subscribe((url: any) => {
      if (url) {
        const e = document.createEvent('MouseEvents');
        const a = document.createElement('a');
        a.download = filename;
        a.href = url;
        a.dataset.downloadurl = ['application/pdf', a.download, a.href].join(':');
        e.initEvent('click', true, false);
        a.dispatchEvent(e);
      }
    });
  }

  // --- Download Original PDFs ---

  downloadOriginalCover(): void {
    if (!this.book || !this.book.cover || !this.book.cover.resource_ID) {
      return;
    }
    const filename = (this.book.title || 'cover') + '_original_cover.pdf';
    this.resourceService.getOriginalURL(this.book.cover.resource_ID, filename).subscribe((url: any) => {
      if (url) {
        window.open(url, '_blank');
      }
    });
  }

  downloadOriginalBody(): void {
    if (!this.book || !this.book.body || !this.book.body.resource_ID) {
      return;
    }
    const filename = (this.book.title || 'body') + '_original_body.pdf';
    this.resourceService.getOriginalURL(this.book.body.resource_ID, filename).subscribe((url: any) => {
      if (url) {
        window.open(url, '_blank');
      }
    });
  }

  // --- EAN13 validation ---

  isValidEAN13(value: string): boolean {
    if (!value || value.length !== 13 || !/^\d{13}$/.test(value)) {
      return false;
    }
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(value[i], 10) * (i % 2 === 0 ? 1 : 3);
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    return checkDigit === parseInt(value[12], 10);
  }

  // --- Formatting ---

  formatTrim(): string {
    if (!this.book || !this.book.body) {
      return '';
    }
    const w = Math.round((this.book.body.width / 72) * 100) / 100;
    const h = Math.round((this.book.body.height / 72) * 100) / 100;
    return this.localizeNumber(w) + ' × ' + this.localizeNumber(h);
  }

  formatShift(val: number): string {
    if (val == null) {
      return this.localizeNumber('0.00');
    }
    return this.localizeNumber((Math.round((val / 72) * 100) / 100).toFixed(2));
  }

  formatScale(val: number): string {
    if (val == null) {
      return this.localizeNumber('100') + '%';
    }
    return this.localizeNumber(Math.round(val * 100)) + '%';
  }

  /** Transform digits to locale-specific characters (e.g., Arabic-Indic numerals) */
  localizeNumber(value: any): string {
    return LanguagePipe.localizeNumber(value);
  }

  getResourceStatusColor(side: any): string {
    return ResourceStatus.fill(!!(side && side.resource_ID), this.resourceFor(side && side.resource_ID));
  }

  /**
   * Bottom stripe encoding `standardized`, drawn only once `prepared`
   * is COMPLETE.
   *
   * Because the stripe reuses the fill palette, a fully-finished side
   * renders a green stripe on a green fill and is invisible - so the
   * badge only gains ink when something is actually outstanding.
   */
  getResourceStripe(side: any): string {
    return ResourceStatus.stripe(this.resourceFor(side && side.resource_ID), side && side.standardized);
  }

  isResourceActionable(side: any): boolean {
    return ResourceStatus.isActionable(this.resourceFor(side && side.resource_ID), side && side.standardized);
  }

  getResourceDisplayString(side: any): string {
    return ResourceStatus.displayString(side && side.resource_ID);
  }

  getResourceHoverText(side: any, label: string): string {
    return ResourceStatus.hoverText(label, !!(side && side.resource_ID),
      this.resourceFor(side && side.resource_ID), side && side.standardized);
  }

  /**
   * Advance whichever stage this side is ready for.
   *
   * Delegates the decision to `ResourceStatus.action` so this view and
   * the three thumbnails cannot disagree about when standardization may
   * be requested. The existing `standardizeCover()` / `standardizeBody()`
   * buttons remain and do the same thing through the same guard.
   */
  onResourceBadgeClick(side: any, isCover: boolean, event: Event): void {
    if (event) { event.stopPropagation(); }
    if (!side) { return; }
    const res = this.resourceFor(side.resource_ID);
    const stage = ResourceStatus.action(res, side.standardized);
    if (stage === 'PREPARE') {
      res.prepared = 'WAITING';
      this.resourceService.updateItem(res).subscribe();
    } else if (stage === 'STANDARDIZE') {
      if (isCover) {
        this.standardizeCover();
      } else {
        this.standardizeBody();
      }
    }
  }

  getPaperTypeName(paperType_ID: number): string {
    if (!paperType_ID) {
      return '';
    }
    const pt = this.paperTypeService.objects.find(p => p.ID === paperType_ID);
    if (pt) {
      return LanguagePipe.get(pt.name, 'compressSpaces');
    }
    return String(paperType_ID);
  }

  getLanguageString(value: string): string {
    if (!value) {
      return '';
    }
    return LanguagePipe.get(value, 'compressSpaces');
  }

  // --- Fit-to-width zoom ---

  /** Compute zoom % so the content fits the container width without horizontal scroll */
  fitCoverToWidth(): void {
    if (this.coverPreviewArea) {
      const containerWidth = this.coverPreviewArea.nativeElement.clientWidth - 32;
      const intrinsicW = this.coverIntrinsicWidth;
      if (intrinsicW > 0 && containerWidth > 0) {
        this.coverZoom = Math.max(25, Math.min(800, Math.floor((containerWidth / intrinsicW) * 100)));
      } else {
        this.coverZoom = 50;
      }
    } else {
      this.coverZoom = 50;
    }
  }

  fitInteriorToWidth(): void {
    if (this.spreadsPreviewArea) {
      const containerWidth = this.spreadsPreviewArea.nativeElement.clientWidth - 32;
      const intrinsicW = this.interiorIntrinsicWidth;
      if (intrinsicW > 0 && containerWidth > 0) {
        this.interiorZoom = Math.max(25, Math.min(800, Math.floor((containerWidth / intrinsicW) * 100)));
      } else {
        this.interiorZoom = 50;
      }
    } else {
      this.interiorZoom = 50;
    }
  }

  // --- Intrinsic dimensions computed from book data (not DOM) ---

  get coverIntrinsicWidth(): number {
    if (!this.book || !this.book.body) { return 0; }
    const DISPLAY_SCALE = 288 / 72;
    const BLEED = 15;
    const trimWidth = this.book.body.width;
    const spine = this.book.body.spine || 0;
    // coverDisplayWidth (from BookPreviewCoverComponent) + border(2px)
    return Math.round((trimWidth * 2 + spine) * DISPLAY_SCALE) + 2 * BLEED + 2;
  }

  get coverIntrinsicHeight(): number {
    if (!this.book || !this.book.body) { return 0; }
    const DISPLAY_SCALE = 288 / 72;
    const BLEED = 15;
    const trimHeight = this.book.body.height;
    // coverDisplayHeight + border(2px) + margin (1em top + 1em bottom ≈ 32px)
    return Math.round(trimHeight * DISPLAY_SCALE) + 2 * BLEED + 2 + 32;
  }

  get interiorIntrinsicWidth(): number {
    if (!this.book || !this.book.body) { return 0; }
    const pageW = Math.round((this.book.body.width / 72) * 288);
    // Two pages side by side: each (pageW + 18) + borders (3px total) + 1px safety margin
    return 2 * (pageW + 18) + 4;
  }

  get interiorIntrinsicHeight(): number {
    if (!this.book || !this.book.body) { return 0; }
    const pageH = Math.round((this.book.body.height / 72) * 288);
    // Each spread: page height + 18 (bleed) + 2 (border) + 16px (margin-top) + 16px (margin-bottom)
    const spreadH = pageH + 18 + 2 + 16 + 16;
    const numSpreads = this.pageNumbers(2).length;
    // Last spread has no bottom margin
    return spreadH * numSpreads - (numSpreads > 0 ? 16 : 0);
  }

  /** Scaled dimensions for the wrappers */
  get scaledCoverWidth(): number {
    const w = this.coverIntrinsicWidth;
    return w > 0 ? Math.ceil(w * this.effectiveCoverZoom / 100) : 0;
  }

  get scaledCoverHeight(): number {
    const h = this.coverIntrinsicHeight;
    return h > 0 ? Math.ceil(h * this.effectiveCoverZoom / 100) : 0;
  }

  get scaledInteriorWidth(): number {
    const w = this.interiorIntrinsicWidth;
    return w > 0 ? Math.ceil(w * this.effectiveInteriorZoom / 100) : 0;
  }

  get scaledInteriorHeight(): number {
    const h = this.interiorIntrinsicHeight;
    return h > 0 ? Math.ceil(h * this.effectiveInteriorZoom / 100) : 0;
  }

  /** Effective zoom: returns 100 if not yet initialized */
  get effectiveCoverZoom(): number {
    return this.coverZoom || 100;
  }

  get effectiveInteriorZoom(): number {
    return this.interiorZoom || 100;
  }

  pageNumbers(countBy: number): number[] {
    if (!this.book || !this.book.body) {
      return [];
    }
    const pages = [];
    const start = Math.max(0, this.interiorPageStart - 1);
    const end = Math.min(this.book.body.numPages, this.interiorPageEnd);
    // Align start to even page for spreads
    const alignedStart = start % 2 === 0 ? start : start - 1;
    for (let i = alignedStart; i <= end; i += countBy) {
      pages.push(i);
    }
    return pages;
  }

  get totalPages(): number {
    if (!this.book || !this.book.body) {
      return 0;
    }
    return this.book.body.numPages;
  }

  showPreviousPages(): void {
    this.interiorPageStart = Math.max(1, this.interiorPageStart - this.PAGE_CHUNK);
    this.interiorPageEnd = this.interiorPageStart + this.PAGE_CHUNK - 1;
  }

  showNextPages(): void {
    this.interiorPageStart = this.interiorPageEnd + 1;
    this.interiorPageEnd = Math.min(this.totalPages, this.interiorPageStart + this.PAGE_CHUNK - 1);
  }

  showAllPages(): void {
    this.interiorPageStart = 1;
    this.interiorPageEnd = this.totalPages;
  }

  resetPageRange(): void {
    this.interiorPageStart = 1;
    this.interiorPageEnd = Math.min(this.PAGE_CHUNK, this.totalPages);
  }

  get hasPreviousPages(): boolean {
    return this.interiorPageStart > 1;
  }

  get hasNextPages(): boolean {
    return this.interiorPageEnd < this.totalPages;
  }
}
